import { FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PasswordInput from '@/components/feature/PasswordInput';
import { useMemberAuth } from '@/contexts/MemberAuthContext';
import { MemberApiError } from '@/services/memberAuth';

const inputClass =
  'w-full px-3.5 md:px-5 py-2.5 md:py-4 rounded-xl border border-[#E8E0D6] text-base outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A]';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || searchParams.get('Token') || '';
  const { resetPassword } = useMemberAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setExpired(false);

    if (!token) {
      setError('重設密碼連結無效，請重新申請。');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('兩次輸入的密碼不一致。');
      return;
    }
    if (newPassword.length < 8) {
      setError('新密碼至少需要 8 碼。');
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token, newPassword);
      setDone(true);
    } catch (err) {
      if (err instanceof MemberApiError) {
        setError(err.message);
        setExpired(Boolean(err.expired));
      } else {
        setError('重設失敗，請稍後再試。');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="mx-auto max-w-md px-6 py-16 md:py-24 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#1B3D2A]/10">
              <i className="ri-checkbox-circle-line text-3xl text-[#1B3D2A]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1B3D2A]">密碼重設成功</h1>
            <p className="mt-3 text-sm text-[#5A6B5A]">已為您自動登入，歡迎回來。</p>
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="mt-6 rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-bold text-white hover:bg-[#143026]"
            >
              前往會員中心
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="mx-auto max-w-md px-6 py-12 md:py-20">
          <div className="mb-8 text-center">
            <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
              Reset Password
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1B3D2A]">設定新密碼</h1>
          </div>

          {!token ? (
            <div className="rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6 md:p-8 text-center">
              <p className="text-sm text-[#5A6B5A]">重設密碼連結無效，請確認信件中的連結是否完整，或重新申請密碼重設。</p>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="mt-6 rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-bold text-white hover:bg-[#143026]"
              >
                重新申請
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6 md:p-8">
              <div>
                <label htmlFor="reset-password" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">新密碼</label>
                <PasswordInput
                  id="reset-password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder="至少 8 碼"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="reset-confirm-password" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">確認新密碼</label>
                <PasswordInput
                  id="reset-confirm-password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="請再次輸入新密碼"
                  className={inputClass}
                />
              </div>

              {error && (
                <div className="rounded-xl bg-[#fdebea] px-4 py-3 text-sm text-[#c3423f]">
                  {error}
                  {expired && (
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="ml-2 font-bold underline"
                    >
                      重新申請
                    </button>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] py-3.5 text-base font-bold text-white transition-colors hover:bg-[#143026] disabled:opacity-60"
              >
                {submitting ? '設定中...' : '設定新密碼'}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
